import type { ScreenshotCapturer } from './screenshotCapture';

/**
 * Resolves the DOM element to capture. Defaults to the document body.
 * Returning null aborts the capture (handled as a best-effort failure upstream).
 */
export type CaptureTarget = () => HTMLElement | null;

/**
 * Infra adapter for the {@link ScreenshotCapturer} port: renders a DOM subtree
 * to a PNG via html2canvas. Web-side DOM capture was chosen over native
 * WebView2/plugin capture because it sidesteps the WebView2 gRPC-web streaming
 * bug and keeps the recorder self-contained (no extra RPC round-trip).
 *
 * Thin shell only — all decision logic (which steps, Full-only, best-effort)
 * lives in {@link ScreenshotCollector}. html2canvas is lazy-imported so this
 * module never loads it (or touches the DOM) until an actual capture runs.
 */
export class Html2CanvasCapturer implements ScreenshotCapturer {
  private readonly target: CaptureTarget;

  constructor(target: CaptureTarget = () => (typeof document !== 'undefined' ? document.body : null)) {
    this.target = target;
  }

  async capture(): Promise<Uint8Array> {
    if (typeof document === 'undefined') {
      throw new Error('No DOM available for screenshot capture.');
    }
    const element = this.target();
    if (!element) {
      throw new Error('No capture target resolved.');
    }

    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(element, { logging: false, useCORS: true });

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      throw new Error('canvas.toBlob returned null.');
    }
    return new Uint8Array(await blob.arrayBuffer());
  }
}
